import React, { useCallback, useEffect, useState } from 'react';
import { Bot, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getCurrentAccount } from '../../lib/auth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { SupplierCombobox } from '../collectors/SupplierCombobox';

const ISSUE = { missing_username: 'username', missing_password: 'password', no_supplier_match: 'supplier' };
const DOT = { connected: 'bg-emerald-500', invalid: 'bg-rose-500', no_bills: 'bg-amber-500', pending: 'bg-slate-300' };
const LABEL = { connected: 'Connected', invalid: 'Invalid', no_bills: 'No bills yet', pending: 'Verifying' };

/**
 * Interactive collector-onboarding card rendered inside the agent chat.
 * Fetches the draft the agent prepared → lets the user fix rows needing input
 * → Confirm & Onboard → polls live connectivity lights. Mirrors ReportCard.
 */
export const CollectorOnboardCard = ({ card }) => {
  const draftId = card?.draft_id;
  // Backend always sends org_id, but fall back to the active account so the
  // card never gets stuck on "Preparing preview…" if it's ever missing.
  const orgId = card?.org_id || getCurrentAccount()?.id || null;
  const [draft, setDraft] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [fixes, setFixes] = useState({});
  const [phase, setPhase] = useState('review'); // review | applying | status
  const [status, setStatus] = useState(null);
  const [pollActive, setPollActive] = useState(false);
  const [updateDuplicates, setUpdateDuplicates] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const params = { params: { account_id: orgId } };

  useEffect(() => {
    if (!draftId || !orgId) {
      if (!orgId) setLoadError('No organisation is in scope for this onboarding preview.');
      return;
    }
    setLoadError(null);
    api.get(`/portal-collectors/onboard/drafts/${draftId}`, params)
      .then(({ data }) => {
        setDraft(data);
        // Re-hydrate the phase after a refresh: an already-applied draft must
        // resume the live status view (outcome) rather than re-showing Confirm.
        if (data?.status === 'applied') setPhase('status');
      })
      .catch((e) => setLoadError(e.response?.data?.detail || 'Could not load the onboarding preview.'));
    api.get('/portal-collectors/suppliers', params).then(({ data }) => setSuppliers(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, orgId]);

  const setFix = (rowId, patch) => setFixes((p) => ({ ...p, [rowId]: { ...p[rowId], ...patch } }));

  const saveRow = async (row) => {
    try {
      const { data } = await api.patch(`/portal-collectors/onboard/drafts/${draftId}/rows/${row.row_id}`, fixes[row.row_id] || {}, params);
      setDraft(data);
      toast.success('Row updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const pollStatus = useCallback(async (attempt) => {
    try {
      const { data } = await api.get(`/portal-collectors/onboard/drafts/${draftId}/status`, params);
      setStatus(data);
      if (data.counts?.pending > 0 && attempt < 24) {
        setPollActive(true);
        setTimeout(() => pollStatus(attempt + 1), 5000);
      } else {
        setPollActive(false);
      }
    } catch { setPollActive(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, orgId]);

  // Entering the status phase — either via Confirm OR by re-opening an
  // already-applied draft after a refresh — kicks off connectivity polling so
  // the card shows the live outcome instead of the (misleading) Confirm button.
  useEffect(() => {
    if (phase === 'status') pollStatus(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const confirm = async () => {
    setPhase('applying');
    try {
      const { data } = await api.post(`/portal-collectors/onboard/drafts/${draftId}/apply`,
        { update_duplicates: updateDuplicates }, params);
      setApplyResult(data);
      const added = data.created || 0, upd = data.updated || 0, skp = data.skipped_duplicates || 0;
      toast.success(data.already_applied
        ? 'Already onboarded — checking connection status'
        : `Added ${added}${upd ? `, updated ${upd}` : ''}${skp ? `, skipped ${skp}` : ''}`);
      setPhase('status'); // the phase effect starts pollStatus(0)
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboard failed');
      setPhase('review');
    }
  };

  const shell = (children) => (
    <div data-testid="ai-collector-onboard-card" className="mt-3 rounded-lg border border-slate-200 bg-white p-3 not-prose">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900 mb-2">
        <Bot className="h-4 w-4 text-blue-600" /> Collector Onboarding
      </div>
      {children}
    </div>
  );

  if (loadError) return shell(<div data-testid="ai-collector-onboard-error" className="text-xs text-rose-600 inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {loadError}</div>);
  if (!draft) return shell(<div className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preparing preview…</div>);

  const ready = draft.summary?.ready || 0;
  const dupCount = draft.summary?.duplicate || 0;
  const dupRows = (draft.rows || []).filter((r) => r.status === 'duplicate' && r.duplicate_scope === 'existing');
  const confirmLabel = (() => {
    const parts = [];
    if (ready > 0) parts.push(`Onboard ${ready}`);
    if (updateDuplicates && dupRows.length > 0) parts.push(`Update ${dupRows.length}`);
    return parts.length ? `Confirm & ${parts.join(' + ')}` : 'Nothing new to onboard';
  })();

  // STATUS phase — live connectivity lights + a clear outcome banner so the
  // user always knows what happened and what to do next (never a blank revert).
  if (phase === 'status') {
    const c = status?.counts || { connected: 0, invalid: 0, no_bills: 0, pending: 0 };
    const total = (c.connected || 0) + (c.invalid || 0) + (c.no_bills || 0) + (c.pending || 0);
    const verifying = (c.pending || 0) > 0 && pollActive;
    const background = (c.pending || 0) > 0 && !pollActive;   // poll window elapsed; bot still working
    const complete = Boolean(status) && total > 0 && (c.pending || 0) === 0;
    const appliedSum = status?.applied_summary
      || (applyResult ? { added: applyResult.created, updated: applyResult.updated, skipped: applyResult.skipped_duplicates } : null);
    return shell(
      <div className="space-y-2" data-testid="onboard-status-phase">
        {!status && (
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading onboarding status…</div>
        )}
        {appliedSum && (
          <div data-testid="onboard-apply-summary" className="text-[11px] text-slate-600">
            Added <b>{appliedSum.added || 0}</b>{(appliedSum.updated || 0) > 0 ? <>, updated <b>{appliedSum.updated}</b></> : null}{(appliedSum.skipped || 0) > 0 ? <>, skipped <b>{appliedSum.skipped}</b> already-onboarded</> : null}. Manage logins in <span className="font-medium">Org → UBM Onboarding → Vendors</span>; collected bills appear in <span className="font-medium">Intake Hub → Bill Jobs</span>.
          </div>
        )}
        {complete && (
          <div data-testid="onboard-outcome-complete" className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" />
            <div className="text-xs text-slate-700">
              <div className="font-semibold text-emerald-800">Onboarding complete</div>
              <div>
                {c.connected} connected{(c.no_bills || 0) > 0 ? `, ${c.no_bills} awaiting a first bill` : ''}{(c.invalid || 0) > 0 ? `, ${c.invalid} need a login re-check` : ''}. Collected bills flow into <span className="font-medium">Intake Hub → Bill Jobs</span> automatically.{(c.invalid || 0) > 0 ? ' Fix invalid logins under Org → UBM Onboarding → Vendors.' : ''}
              </div>
            </div>
          </div>
        )}
        {verifying && (
          <div data-testid="onboard-outcome-verifying" className="text-[11px] text-slate-500 inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Verifying {c.pending} credential(s) — this can take a few minutes…</div>
        )}
        {background && (
          <div data-testid="onboard-outcome-background" className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2">
            <Loader2 className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
            <div className="text-xs text-slate-700">
              <div className="font-semibold text-amber-800">Still verifying in the background</div>
              <div>{c.connected} connected so far. {c.pending} still being checked by the collector bot — this can take several minutes. It's safe to close this; progress is saved. Just ask me “any update on the onboarding?” later.</div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />{c.connected} connected</span>
          {(c.no_bills || 0) > 0 && (
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />{c.no_bills} no bills yet</span>
          )}
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" />{c.invalid} invalid</span>
          <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-slate-300" />{c.pending} verifying</span>
        </div>
        <div className="divide-y border-t">
          {(status?.credentials || []).map((r) => (
            <div key={r.credential_id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`onboard-status-${r.credential_id}`}>
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${DOT[r.status] || DOT.pending}`} />
              <span className="font-medium truncate">{r.supplier || '—'}</span>
              <span className="text-slate-400 font-mono">{r.account_number || ''}</span>
              <span className="ml-auto text-slate-500">{LABEL[r.status] || r.status}</span>
            </div>
          ))}
        </div>
      </div>,
    );
  }

  // REVIEW phase
  return shell(
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant="outline" className="rounded-none border-emerald-500 text-emerald-700">{ready} ready</Badge>
        {(draft.summary?.needs_input || 0) > 0 && (
          <Badge variant="outline" className="rounded-none border-amber-500 text-amber-700">{draft.summary.needs_input} need input</Badge>
        )}
        {dupCount > 0 && (
          <Badge variant="outline" className="rounded-none border-blue-500 text-blue-700">{dupCount} already onboarded</Badge>
        )}
      </div>
      {dupRows.length > 0 && (
        <div data-testid="onboard-duplicates" className="rounded-md border border-blue-200 bg-blue-50 p-2 space-y-1.5">
          <div className="text-xs font-semibold text-blue-800">{dupRows.length} credential(s) already onboarded</div>
          <div className="space-y-1 max-h-28 overflow-y-auto">
            {dupRows.map((r) => (
              <div key={r.row_id} data-testid={`onboard-duplicate-${r.row_id}`} className="flex items-center gap-2 text-[11px] text-slate-600">
                <span className="font-medium truncate">{r.supplier_name || r.account_name || '—'}</span>
                <span className="font-mono text-slate-400">{r.account_number || ''}</span>
                <span className="ml-auto shrink-0">{(r.changes && r.changes.length) ? `changes: ${r.changes.join(', ')}` : 'no changes'}</span>
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={updateDuplicates} onChange={(e) => setUpdateDuplicates(e.target.checked)} data-testid="onboard-update-duplicates-toggle" />
            Update these with the new values (otherwise they're skipped)
          </label>
        </div>
      )}
      <div className="max-h-56 overflow-y-auto border">
        <table className="w-full text-xs">
          <tbody>
            {draft.rows.filter((r) => r.status !== 'duplicate').map((row) => {
              const needs = row.needs_input || [];
              return (
                <tr key={row.row_id} className="border-t align-top" data-testid={`card-row-${row.row_id}`}>
                  <td className="p-1.5">
                    <div className="font-medium">{row.account_name || '—'}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{row.account_number || ''}</div>
                  </td>
                  <td className="p-1.5 min-w-[150px]">
                    {needs.includes('no_supplier_match') ? (
                      <SupplierCombobox suppliers={suppliers} value={fixes[row.row_id]?.supplier_short}
                        onChange={(s, n) => setFix(row.row_id, { supplier_short: s, supplier_name: n })}
                        testId={`card-supplier-${row.row_id}`} />
                    ) : (<Badge variant="outline" className="rounded-none">{row.supplier_name}</Badge>)}
                  </td>
                  <td className="p-1.5">
                    {needs.includes('missing_username') ? (
                      <Input className="rounded-none h-7 text-xs" placeholder="username"
                        onChange={(e) => setFix(row.row_id, { username: e.target.value })} data-testid={`card-username-${row.row_id}`} />
                    ) : (<span className="font-mono">{row.username}</span>)}
                  </td>
                  <td className="p-1.5">
                    {needs.includes('missing_password') ? (
                      <Input type="password" className="rounded-none h-7 text-xs" placeholder="password"
                        onChange={(e) => setFix(row.row_id, { password: e.target.value })} data-testid={`card-password-${row.row_id}`} />
                    ) : (<span className="font-mono">••••{row.password_last4 || ''}</span>)}
                  </td>
                  <td className="p-1.5 whitespace-nowrap">
                    {row.status === 'ready' ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /></span>
                    ) : (
                      <div className="space-y-1">
                        <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3.5 w-3.5" />{needs.map((n) => ISSUE[n] || n).join(', ')}</span>
                        <Button size="sm" variant="outline" className="rounded-none h-6 text-[11px] w-full" onClick={() => saveRow(row)} data-testid={`card-fix-${row.row_id}`}>Save</Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Button size="sm" className="rounded-none w-full"
        disabled={(ready === 0 && !(updateDuplicates && dupRows.length > 0)) || phase === 'applying'}
        onClick={confirm} data-testid="card-confirm-onboard">
        {phase === 'applying' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Bot className="h-3.5 w-3.5 mr-1" />}
        {confirmLabel}
      </Button>
    </div>,
  );
};

export default CollectorOnboardCard;
